import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { styled, useTheme } from "@mui/material";
import type { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  createRootRoute,
  Outlet,
  redirect,
  useRouter,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import { Suspense, useEffect, useRef, useState } from "react";
import { apiInventoryGet } from "~/app/components/Profile/api/apiInventoryGet";
import { usePWAReward } from "~/app/hooks/usePWAReward";
import { useWelcomeGift } from "~/app/hooks/useWelcomeGift";
import { updateAnalyticsUser } from "~/app/services/scopely/titanAnalytics";
import { useNavigationStore } from "~/app/stores/navigation";
import { apiUserGet, DrawerLeft, DrawerRight } from "~/components";
import {
  DIRECT_PAYMENT_LISTING,
  PIGGYBANK_USE_DIRECTPAYMENT,
  SCROLL_TO,
  SHOW_PRIZE_WHEEL,
} from "~/constants/urls.ts";
import { TRACKING_ERROR_CODES, trackSysConnect } from "~/services/scopely/loginAnalyticsTracking";
import { scopelyId, tracker } from "~/services/scopely/scopelyId.ts";
import { useAuthStore, useUser } from "~/stores/auth";
import { loginByToken } from "../loginByToken";
import { NotFoundPage } from "../NotFoundPage";

function parseLogoutState(gameState: string) {
  let result = {};
  try {
    result = JSON.parse(window.atob(gameState));
  } catch (err) {}
  return result;
}

type AppBarProps = MuiAppBarProps & {
  open?: boolean;
};

const AppBarMobile = styled(AppBar)<AppBarProps>(({ theme }) => ({
  display: "none",

  [theme.breakpoints.down("lg")]: {
    display: "flex",
  },
}));

const MainMobile = styled("main")(({ theme }) => ({
  display: "none",
  flexDirection: "column",
  zIndex: "1",
  padding: "0 16px",
  paddingBottom: "24px",
  width: "100%",

  [theme.breakpoints.down("lg")]: {
    display: "flex",
  },
}));

const MainDesktop = styled("main")(({ theme }) => ({
  display: "flex",
  width: "100%",
  maxWidth: "2440px",
  position: "relative",
  zIndex: 1,
  padding: "16px",
  paddingBottom: "24px",

  [theme.breakpoints.down("lg")]: {
    display: "none",
  },
}));

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    const search = new URLSearchParams(location.search);
    const userid = search.get("userid");
    const token = search.get("token");

    // handle logout redirect
    if (location.pathname === "/logout") {
      const scopelyIdentity = await scopelyId;

      const currentLocation = window.location.href;
      if (!scopelyIdentity?.isLogoutUri(currentLocation)) {
        return;
      }

      try {
        const result = await scopelyIdentity.logoutResult(currentLocation);
        const encodedState = result?.getResponse()?.state;
        const gameState = scopelyIdentity.getGameState(encodedState as string);
        const originalState: { accountNotLinked?: boolean } = parseLogoutState(gameState);
        if (originalState.accountNotLinked) {
          // redirect to the home, trim login tokens from the url and show link account modal
          return redirect({
            to: "/home",
            search: { accountNotLinked: true },
            replace: true,
          });
        }
      } catch (err) {
        console.error("Error in handleLogoutRedirect:", err);
      }
      return redirect({ to: "/home" });
    }

    if (userid && token) {
      try {
        await loginByToken(userid, token);

        // Create new search params with existing params
        const newSearch = new URLSearchParams(search);

        const hasSpecialQueryParams =
          newSearch.has(SHOW_PRIZE_WHEEL) ||
          newSearch.has(PIGGYBANK_USE_DIRECTPAYMENT) ||
          newSearch.has(DIRECT_PAYMENT_LISTING) ||
          newSearch.has(SCROLL_TO);
        const shouldShowMenu = !hasSpecialQueryParams;
        if (shouldShowMenu) {
          // Automatically show side menu only if no query params with special behavior
          newSearch.set("showMenu", "true");
        }

        // Explicitly remove sensitive params
        newSearch.delete("userid");
        newSearch.delete("token");

        // Update URL without reloading
        window.history.replaceState(
          {},
          "",
          `${location.pathname}${newSearch.toString() ? `?${newSearch.toString()}` : ""}`,
        );
      } catch (error) {
        console.error("Login by token failed:", error);
        trackSysConnect({
          type: "Fastlogin",
          errorCode: `${TRACKING_ERROR_CODES.FAST_LOGIN_DATA_IS_NOT_SUCCESS}${TRACKING_ERROR_CODES.UNKNOWN_ERROR}`,
        });

        throw redirect({ to: "/home" });
      }
    }

    if (location.pathname === "/") {
      throw redirect({ to: "/home" });
    }
  },
  component: () => {
    const router = useRouter();
    const { setPreviousLocation } = useNavigationStore();

    const { showMenu } = useSearch({ strict: false }) as { showMenu: boolean };
    const isProduction = ["production"].includes(
      import.meta.env.VITE_APP_ENVIRONMENT?.toLowerCase(),
    );

    const { isAuthenticated } = useAuthStore();

    const theme = useTheme();
    const isDownLg = useMediaQuery(theme.breakpoints.down("lg"));

    const { setUserData } = useAuthStore();
    const user = useUser();

    const [openMenu, setOpenMenu] = useState(showMenu);
    const [openLogin, setOpenLogin] = useState(false);

    const handleOpenMenu = () => {
      setOpenMenu(true);
    };

    const handleCloseMenu = () => {
      setOpenMenu(false);
    };

    const handleOpenLogin = () => {
      setOpenLogin(true);
    };

    const handleCloseLogin = () => {
      setOpenLogin(false);
    };

    const { data: inventory, isLoading: isLoadingInventory } = useQuery({
      queryKey: ["apiInventoryGet"],
      queryFn: () => apiInventoryGet(),
      placeholderData: keepPreviousData,
      retry: 2,
      retryDelay: 300,
      enabled: isAuthenticated,
    });

    const { data: userData, isLoading: isLoadingUser } = useQuery({
      queryKey: ["apiUserGet"],
      queryFn: () => apiUserGet(Number(user?.Id || 0)),
      placeholderData: keepPreviousData,
      retry: 2,
      retryDelay: 300,
      enabled: isAuthenticated,
    });

    const { ModalWelcomeGift, isWelcomeGiftClaimed, welcomeGiftPrizeAmount } = useWelcomeGift();
    const { ModalPWAReward, isPWARewardClaimed, pwaRewardPrizeAmount } = usePWAReward();
    const pathname = useRouterState({
      select: (state) => state.location.pathname,
    });

    useEffect(() => {
      const unsub = router.subscribe("onResolved", (event) => {
        setPreviousLocation(event.fromLocation ?? null);
      });
      return unsub;
    }, [router, setPreviousLocation, pathname]);

    useEffect(() => {
      if (userData && inventory) {
        const diamondsTotal = inventory?.Data?.OwnedItems?.Diamonds?.Count || 0;
        const login_type = localStorage.getItem("login_type");
        const ssoId = user?.SsoId?.Facebook ? user?.SsoId?.Facebook : user?.SsoId?.ScopelyIdentity;

        updateAnalyticsUser(user?.Id || 0);
        setUserData(userData);
        if (!localStorage.getItem("sent_sys_connect")) {
          trackSysConnect({
            type: login_type as string,
            params: {
              coins: inventory?.Data?.OwnedItems?.Coins?.Count || 0,
              diamonds: diamondsTotal,
              energy: inventory?.Data?.OwnedItems?.Energy?.Count,
              hints: inventory?.Data?.OwnedItems?.Hints?.Count || 0,
              ssoId: ssoId as string,
            },
          });
          localStorage.setItem("sent_sys_connect", "true");
        }
      }
    }, [inventory, userData]);

    const isLoading = isLoadingInventory || isLoadingUser;

    // for debug purposes
    const [deviceToken, setDeviceToken] = useState<undefined | string>(undefined);
    const showDeviceToken = () => {
      setDeviceToken(tracker.getDeviceToken());
    };

    return (
      <>
        {isWelcomeGiftClaimed === false && (
          <Suspense fallback={null}>
            <ModalWelcomeGift
              onOpen={() => setOpenMenu(false)}
              diamondsPrizeAmount={welcomeGiftPrizeAmount}
            />
          </Suspense>
        )}

        {isDownLg && (
          <>
            <AppBarMobile position="fixed">
              <Toolbar
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleOpenMenu}
                  edge="start"
                >
                  <MenuIcon />
                </IconButton>

                {!isProduction && (
                  <button
                    type="button"
                    style={{
                      border: "none",
                      backgroundColor: "inherit",
                      width: "100%",
                      minHeight: "50px",
                    }}
                    onClick={showDeviceToken}
                    onKeyDown={showDeviceToken}
                  >
                    {deviceToken}
                  </button>
                )}

                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={handleOpenLogin}
                  edge="start"
                >
                  <AccountCircleOutlinedIcon />
                </IconButton>
              </Toolbar>
            </AppBarMobile>

            <MainMobile>
              <Outlet />
            </MainMobile>

            <Drawer
              sx={{
                maxWidth: "252px",
                height: "100%",
                maxHeight: "100vh",
                overflow: "auto",
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  maxWidth: "252px",
                  boxSizing: "border-box",
                },
                zIndex: "9999",
              }}
              variant="temporary"
              anchor="left"
              open={openMenu}
              onClose={handleCloseMenu}
            >
              <DrawerLeft handleCloseMenu={handleCloseMenu} />
            </Drawer>

            <Drawer
              sx={{
                width: "285px",
                height: "100%",
                maxHeight: "100vh",
                overflow: "auto",
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  width: "285px",
                  boxSizing: "border-box",
                },
                zIndex: "9999",
              }}
              variant="temporary"
              anchor="right"
              open={openLogin}
              onClose={handleCloseLogin}
              hideBackdrop={false}
            >
              <DrawerRight isLoading={isLoading} handleCloseLogin={handleCloseLogin} />
            </Drawer>
          </>
        )}

        {!isDownLg && (
          <MainDesktop>
            <Box
              position="sticky"
              left="16px"
              zIndex={"-1"}
              top="16px"
              bottom="16px"
              maxHeight="901px"
              overflow="auto"
              sx={{
                minWidth: { xs: "", md: "240px" },
              }}
            >
              <DrawerLeft />
            </Box>

            <Box flex="1 1 calc(100% - 520px)" width="calc(100% - 520px)" zIndex={1} px={2}>
              <Outlet />
            </Box>

            <Box
              position="sticky"
              right="16px"
              maxWidth="285px"
              zIndex={"-1"}
              sx={{
                top: "16px",
                bottom: "16px",
                maxHeight: "901px",
                position: "sticky",
                overflow: "auto",
              }}
            >
              <DrawerRight isLoading={isLoading} />
            </Box>
          </MainDesktop>
        )}
      </>
    );
  },
  notFoundComponent: () => <NotFoundPage />,
});
